import type { ReactNode } from 'react'
import React from 'react'
import { useParams } from 'react-router-dom'

import { Dashboard, PageLayout, SideSummary } from '@components/common'
import {
  BackButton,
  ButtonClean,
  Dropdown,
  Icon,
  Loading,
  ToolbarButton,
  useMediaQueryContext,
} from '@fourthwall/components'
import { DotsHorizontalIcon, PinIcon } from '@fourthwall/icons'
import { isWebView } from '@fourthwall/utils'
import { useFocusFirstErrorOnSubmit } from '@fourthwall/utils/dist/formik'

import { useCurrentShopQuery } from '../../../../../hooks/useCurrentShopQuery'
import { useTiers } from '../../../Perks/hooks/useTiers'
import { useDeletePost } from '../../hooks/useDeletePost'
import { usePost } from '../../hooks/usePost'
import { usePostUpdate } from '../../hooks/usePostUpdate'
import { PostAccess } from '../../modules/PostAccess'
import { PostAnalytics } from '../../modules/PostAnalytics'
import { PostPublishedDate } from '../../modules/PostPublishedDate'
import { PostTag } from '../../modules/PostTag'
import { TagsFields } from '../../modules/TagsFields'
import { getBackUrl } from '../../utils'
import * as S from './PostLayout.styled'

type Props = {
  disabled?: boolean
  children: ReactNode
  liveStreamStartDate?: string
  liveStreamPreview?: ReactNode
}

export const PostLayout = ({
  disabled,
  children,
  liveStreamStartDate,
  liveStreamPreview,
}: Props) => {
  const media = useMediaQueryContext()
  const { postId = '' } = useParams<'postId'>()
  const { post, postQuery } = usePost(postId)
  const { tiers } = useTiers()
  const { currentShopQuery } = useCurrentShopQuery()
  const { isLoading: isPostLoading, updatePost, pinPost, unpinPost } = usePostUpdate(postId)
  const { isLoading: isDeleteLoading, handleDeletePost } = useDeletePost()

  useFocusFirstErrorOnSubmit()

  if (!post || !currentShopQuery.data || !tiers) {
    return null
  }

  const renderPinnedOption = () => {
    if (post.statuses.post !== 'Live' && post.statuses.post !== 'Finished') return null

    return (
      <Dropdown.Item
        label={post.pinnedAt ? 'Unpin post' : 'Pin post'}
        onClick={() => {
          post.pinnedAt ? unpinPost() : pinPost()
        }}
      />
    )
  }

  const renderToolbar = () => {
    return (
      <Dropdown
        content={
          <>
            {renderPinnedOption()}
            <Dropdown.Item
              label="Delete"
              onClick={() => {
                handleDeletePost(post.id)
              }}
            />
          </>
        }
        position="bottom-end"
      >
        <ToolbarButton
          label="Settings"
          icon={DotsHorizontalIcon}
          size={media.tablet ? 'medium' : 'small'}
        />
      </Dropdown>
    )
  }

  const renderPinnedInfo = () => {
    if (!post.pinnedAt) return null

    return (
      <S.PinnedInfo>
        <Icon component={PinIcon} height={14} />
        Pinned post <ButtonClean label="Unpin" onClick={unpinPost} />
      </S.PinnedInfo>
    )
  }

  return (
    <Loading isLoading={isPostLoading || isDeleteLoading || postQuery.isFetching}>
      <PageLayout
        rightContent={
          <SideSummary>
            <PostAccess
              tiers={tiers}
              showNotification={post.statuses.schedule === 'Future'}
              disabled={disabled}
            />
            {post.statuses.schedule === 'Done' && <PostPublishedDate />}
            <PostAnalytics commentsCount={post.comments.count} likesCount={post.likes.count} />
          </SideSummary>
        }
        leftContent={
          <>
            {children}
            <TagsFields disabled={disabled} />
          </>
        }
      >
        <S.HeaderContainer>
          <Dashboard.ItemViewHeader
            name={post.title}
            title={renderPinnedInfo()}
            backButton={<BackButton label="Posts" to={getBackUrl(post.statuses.schedule)} />}
            link={
              !isWebView()
                ? {
                    label: `${currentShopQuery.data.baseUri}${post.path}`,
                    url: `${currentShopQuery.data.baseUri}${post.path}`,
                  }
                : undefined
            }
            status={
              <PostTag
                post={post}
                liveStreamStartDate={liveStreamStartDate}
                updatePost={updatePost}
              />
            }
            toolbar={renderToolbar()}
          />
          {liveStreamPreview}
        </S.HeaderContainer>
      </PageLayout>
    </Loading>
  )
}
